import React, { useState } from "react";
import { FiDownload } from "react-icons/fi";
import { useMutation, useQuery } from "react-query";
import { Cell, Column } from "react-table";
import {
  Badge,
  Box,
  Button,
  Flex,
  Spacer,
  ThemingProps,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { AxiosError, AxiosResponse } from "axios";
import fileDownload from "js-file-download";
import { startCase } from "lodash";

import { Table } from "@alpacahq/alpaca-component-library";

import { downloadReport, listReports } from "../../api/api";
import {
  DownloadReportRequest,
  ListReportParams,
  Report,
  ReportStatus,
  ReportType,
} from "../../api/types";
import {
  extractFilenameFromHeader,
  extractMimeTypeFromHeader,
  formatDate,
} from "../../globals/utils";
import { FilterButton, FilterDrawer, FilterSelect } from "../filter";
import UUID from "../general/UUID";

interface FilterType {
  status: ReportStatus | "all";
  type: ReportType | "all";
}

const QUERY_STALE_TIME = 5 * 60 * 1000;
const DEFAULT_PAGE_ITEM = 25;
const DEFAULT_FILTER_INPUTS: FilterType = {
  status: "all",
  type: "all",
};

const REPORT_STATUS_OPTIONS = [
  "all",
  "available",
  "expired",
  "failed",
  "queued",
];

const REPORT_TYPE_OPTIONS = ["all", "account_list"];
const REPORT_STATUS_VARIANTS: Record<
  ReportStatus,
  ThemingProps<"Badge">["colorScheme"]
> = {
  available: "green",
  expired: "red",
  failed: "red",
  queued: "orange",
};

const ReportsTable = (): React.ReactElement => {
  const toast = useToast();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [filters, setFilters] = useState<FilterType>(DEFAULT_FILTER_INPUTS);
  const [inputs, setInputs] = useState<FilterType>(DEFAULT_FILTER_INPUTS);

  const { data, isLoading } = useQuery(
    ["reports", filters],
    () => {
      const params: ListReportParams = {};

      if (filters.status !== "all") {
        params.status = filters.status;
      }

      if (filters.type !== "all") {
        params.type = filters.type;
      }

      return listReports(params);
    },
    {
      staleTime: QUERY_STALE_TIME,
      refetchIntervalInBackground: true,
      refetchOnMount: "always",
      onError: (err) =>
        toast({
          title: "An error occurred fetching the reports",
          description: (err as Error).message,
          status: "error",
        }),
    }
  );

  const reports = data ?? [];

  const handleStatusSelect = (value: string): void => {
    setInputs((inputs) => ({ ...inputs, status: value as ReportStatus }));
  };

  const handleTypeSelect = (value: string): void => {
    setInputs((inputs) => ({ ...inputs, type: value as ReportType }));
  };

  const mutation = useMutation<
    AxiosResponse<Blob>,
    AxiosError,
    DownloadReportRequest
  >((payload) => downloadReport(payload), {
    onSuccess: (res) => {
      const mimeType = extractMimeTypeFromHeader(res.headers["content-type"]);
      const filename = extractFilenameFromHeader(
        res.headers["content-disposition"]
      );

      toast({
        title: "Your download will begin shortly",
        status: "info",
      });

      fileDownload(res.data, filename, mimeType);
    },
    onError: (err) => {
      toast({
        title: "An error occurred downloading reports",
        description: err.message,
        status: "error",
      });
    },
  });

  const onSelectedRowsChange = (selectedRows: Record<number, boolean>) => {
    setSelectedRows(
      Object.keys(selectedRows)
        .map((row) => parseInt(row))
        .map((row) => reports[row].id)
    );
  };

  const onDownload = () => {
    mutation.mutate({ report_ids: selectedRows });
  };

  const onApplyFilters = () => {
    setFilters(inputs);
    setSelectedRows([]);
    onCloseFilter();
  };

  const onOpenFilter = () => {
    setIsFilterOpen(true);
    setInputs(filters); // resets filter drawer inputs
  };

  const onCloseFilter = () => {
    setIsFilterOpen(false);
  };

  const onRemoveFilter = (key: string) => {
    setFilters((filters) => ({ ...filters, [key]: null }));
    setSelectedRows([]);
  };

  const columns: Column<Report>[] = [
    {
      id: "id",
      Header: "ID",
      accessor: "id",
      Cell: ({ value }: Cell) => <UUID value={value} />,
    },
    {
      id: "type",
      Header: "Type",
      accessor: "type",
      Cell: ({ value }: Cell) => startCase(value),
    },
    {
      id: "status",
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => (
        <Badge
          colorScheme={REPORT_STATUS_VARIANTS[value]}
          textTransform="capitalize"
        >
          {value}
        </Badge>
      ),
    },
    {
      id: "created_at",
      Header: "Created At",
      accessor: "created_at",
      Cell: ({ value }) => formatDate(value, "MMM DD, YYYY hh:mm A z"),
    },
    {
      id: "expires_at",
      Header: "Expires At",
      accessor: "expires_at",
      Cell: ({ value }) => formatDate(value, "MMM DD, YYYY hh:mm A z"),
    },
  ];

  return (
    <Box>
      <Flex alignItems="center">
        <Spacer />
        <Button
          leftIcon={<FiDownload />}
          bgColor="transparent"
          color={useColorModeValue("black", "white")}
          disabled={selectedRows.length === 0}
          onClick={onDownload}
          variant="ghost"
        >
          Download {selectedRows.length >= 2 && `(${selectedRows.length})`}
        </Button>
        <FilterButton
          filterPills={filters ?? {}}
          openFilter={onOpenFilter}
          removeFilter={onRemoveFilter}
        />
      </Flex>

      <FilterDrawer
        isOpen={isFilterOpen}
        onApply={onApplyFilters}
        onClose={onCloseFilter}
      >
        <FilterSelect
          header="Status"
          options={REPORT_STATUS_OPTIONS}
          onSelect={handleStatusSelect}
          selected={inputs.status ?? "all"}
        />
        <FilterSelect
          header="Type"
          options={REPORT_TYPE_OPTIONS}
          onSelect={handleTypeSelect}
          selected={inputs.type ?? "all"}
        />
      </FilterDrawer>
      <Table
        isLoading={isLoading}
        columns={columns}
        data={reports}
        defaultPerPage={DEFAULT_PAGE_ITEM}
        onSelectedRowsChange={onSelectedRowsChange}
        paginationPosition="top"
        pagination
        selectable
      />
    </Box>
  );
};

export default ReportsTable;
